<!-- ColorSizeSection.vue - Main component for selecting colors and sizes -->
<template>
  <div class="color-size-selector">
    <p class="section-description">
        {{description}}
    </p>

      <div v-if="hasMoreColors" class="load-more-section">
          <div v-if="showValidation" class="validation-message">
              <span class="validation-text" :class="validationClass">
                <svg
                  v-if="validationClass === 'warning'"
                  class="validation-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                {{ validationMessage }}
              </span>
          </div>
      </div>
    <!-- Available Colors Grid with inline ColorSizeLine -->
    <div class="colors-grid-wrapper" v-if="availableColors.length > 0">
      <template v-for="(row, rowIndex) in colorRows" :key="`row-${rowIndex}`">
        <!-- Row of ColorCards -->
        <div class="colors-grid" :ref="el => setGridRef(el, rowIndex)">
          <ColorCard
            v-for="color in row"
            :key="color.id"
            :ref="el => setColorCardRef(el, color.id)"
            :color="color"
            :quantity="colorQuantities[color.id] || 0"
            :is-selected="isColorSelected(color.id)"
            :is-expanded="isColorExpanded(color.id)"
            @select="handleColorSelect"
          />
        </div>
        
        <!-- ColorSizeLine immediately after the row, if an expanded color is in this row -->
        <transition :name="isQtyOnlyProduct ? '' : 'slide-fade'" @after-enter="updateArrowPosition">
          <div 
            v-if="getExpandedColorInRow(row)"
            class="color-size-line-wrapper"
            :class="{ 'is-qty-only': isQtyOnlyProduct }"
            :ref="el => setLineWrapperRef(el, rowIndex)"
          >
            <!-- Arrow indicator -->
            <div 
              v-if="!isQtyOnlyProduct"
              class="arrow-indicator" 
              :style="{ left: arrowPosition }"
            >
              <div class="arrow-up"></div>
            </div>
            
            <ColorSizeLine
              :line="getExpandedLineData(row)"
              :available-sizes="availableSizes"
              :is-qty-only="isQtyOnlyProduct"
              :show-arrow="isQtyOnlyProduct"
              :qty-increment="qtyIncrement"
              :min-order-qty="minQuantity"
              :imprint-key="imprintKey"
              @update-quantity="handleUpdateQuantity"
              @remove="handleRemoveLine"
            />
          </div>
        </transition>
      </template>
    </div>

    <!-- Load More Link -->
    <div v-if="hasMoreColors" class="load-more-section">
      <a 
        class="load-more-link" 
        @click.prevent="loadMoreColors"
        :class="{ 'disabled': isLoadingMore }"
        href="#"
      >
        <span v-if="!isLoadingMore">
          Load More Colors ({{ remainingColorsCount }} remaining)
        </span>
        <span v-else>
          Loading...
        </span>
      </a>
    </div>

    <!-- Loading state -->
    <div v-else-if="availableColors.length === 0" class="loading-state">
      <p>Loading colors and sizes...</p>
    </div>

    <!-- Validation Message -->
    <div v-if="showValidation" class="validation-message">
      <span class="validation-text" :class="validationClass">
        <svg
          v-if="validationClass === 'warning'"
          class="validation-icon"
          width="16"
          height="16"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
        </svg>
        {{ validationMessage }}
      </span>
    </div>

    <div>
      <p>Minimum Order Quantity of {{minQuantity}} | Mix-n-Match in Increments of {{qtyIncrement}}</p>
    </div>

    <!-- Next Step Button -->
    <div class="action-buttons">
      <button
        class="btn-next"
        @click="handleNextStep"
      >
        Next Step: Customize Your Product
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useSelectionStore } from '@/features/imprint/stores/useSelectionStore'
import { useImprintStore } from '@/features/imprint/stores/useImprintStore'
import { useAppStore } from '@/features/imprint/stores/useAppStore'
import { useSectionValidation } from '@/features/imprint/composables/useSectionValidation'
import ColorCard from './ColorCard.vue'
import ColorSizeLine from './ColorSizeLine.vue'
import { mockProduct } from '@/../public/mock-data/products'
import { useMockData } from '@/../public/mock-data/environment'

export default {
  name: 'ColorSizeSection',

  components: {
    ColorCard,
    ColorSizeLine
  },

  props: {
    imprintKey: {
      type: String,
      default: 'default-imprint'
    },
    description: {
      type: String,
      default: ''
    },
    resetTrigger: {
      type: Number,
      default: 0
    }
  },
  
  setup() {
    const { step1Validation } = useSectionValidation();
    
    return {
      step1Validation
    };
  },

  data() {
    return {
      selectionStore: useSelectionStore(),
      imprintStore: useImprintStore(),
      appStore: useAppStore(),
      expandedColorId: null, // null = falls back to defaultActiveColorId
      userHasClickedColor: false, // whether user explicitly clicked a color card
      lastActiveColorId: null, // Track last active color even when collapsed
      colorQuantities: {},
      columnsPerRow: 6,
      gridRefs: {},
      colorCardRefs: {},
      lineWrapperRefs: {},
      arrowPosition: '0px',
      tempLineData: {},
      // Track if user modified quantities for colors (for isQtyOnly logic)
      userModifiedColors: new Set(),
      // Track colors that were auto-initialized (should not be marked as modified)
      autoInitializedColors: new Set(),
      // Initialize API data arrays
      colorVariants: [],
      sizeVariants: [],
      colorHandle: null,
      sizeHandle: null,
      associatedIds: null,
      // Pagination
      COLORS_PER_PAGE: 25,
      currentPage: 1,
      isLoadingMore: false,
      isDataLoaded: false
    }
  },

  computed: {
    minQuantity() {
      // Get min_order_qty from configurable_array for current imprint method
      const sku = this.imprintStore.selectedImprintMethod?.sku;
      if (!sku || !this.imprintStore.fullJson?.configurable_array) {
        return 18; // Default fallback
      }
      
      const methodData = this.imprintStore.fullJson.configurable_array[sku];
      const minQty = methodData?.min_order_qty;
      
      // Parse and validate the value
      const parsedQty = parseInt(minQty, 10);
      return !isNaN(parsedQty) && parsedQty > 0 ? parsedQty : 18;
    },

    qtyIncrement() {
      // Get qty_increment from configurable_array for current imprint method
      const sku = this.imprintStore.selectedImprintMethod?.sku;
      if (!sku || !this.imprintStore.fullJson?.configurable_array) {
        return 1; // Default fallback
      }
      
      const methodData = this.imprintStore.fullJson.configurable_array[sku];
      const increment = methodData?.qty_increment;
      
      // Parse and validate the value
      const parsedIncrement = parseInt(increment, 10);
      return !isNaN(parsedIncrement) && parsedIncrement > 0 ? parsedIncrement : 1;
    },

    /**
     * All color variants with disabled colors filtered out.
     * A color is considered disabled when ALL its sizes in attributes_relation_array
     * have status 'Disabled' for the current imprint method.
     * Falls back to showing all colors when feed data is unavailable.
     */
    enabledColorVariants() {
      if (useMockData()) {
        const response = JSON.parse(mockProduct.colorProduct)
        return response.variants
      }
      const cached = this.selectionStore.getCachedApiData()
      const allColors = cached.colorVariants || []

      const fullJson = this.imprintStore.fullJson
      const currentSku = this.imprintStore.selectedImprintMethod?.sku
      if (!fullJson || !currentSku) return allColors

      const attributesRelation = fullJson.configurable_array?.[currentSku]?.attributes_relation_array
      if (!attributesRelation) return allColors

      const result = []
      const hidden = []

      allColors.forEach(color => {
        const colorTitle = color.title
        if (!colorTitle) { result.push(color); return }

        const colorRelation = attributesRelation[colorTitle]

        if (!colorRelation || !Array.isArray(colorRelation)) {
          hidden.push({ color: colorTitle, reason: 'not in attributes_relation_array' })
          return
        }

        const hasEnabled = colorRelation.some(item => item.status !== 'Disabled')
        if (hasEnabled) {
          result.push(color)
        } else {
          hidden.push({
            color: colorTitle,
            reason: 'all sizes Disabled',
            sizes: colorRelation.map(i => i.size)
          })
        }
      })

      if (hidden.length) {
        console.log(`[VariantFilter] 🚫 Hidden colors (${hidden.length}):`, hidden)
      }
      console.log(`[VariantFilter] ✅ Visible colors: ${result.length} / ${allColors.length}`)

      return result
    },

    availableColors() {
      // Apply pagination to the already-filtered list
      const endIndex = this.currentPage * this.COLORS_PER_PAGE
      return this.enabledColorVariants.slice(0, endIndex)
    },

    totalAvailableColors() {
      return this.enabledColorVariants.length
    },

    hasMoreColors() {
      return this.availableColors.length < this.totalAvailableColors
    },

    remainingColorsCount() {
      return this.totalAvailableColors - this.availableColors.length
    },

    // Returns the color id that should have the quantity selector visible per client requirements:
    // Rule 1: if any qty > 0 exists — show the first color with qty > 0
    // Rule 2: otherwise — show the first available color
    defaultActiveColorId() {
      if (!this.isDataLoaded || this.availableColors.length === 0) return null

      // Rule 1: first color in availableColors order that has qty > 0
      const firstWithQty = this.availableColors.find(color =>
        this.selectedLines.some(line => line && line.slectedColor?.id === color.id && line.totalQty > 0)
      )
      if (firstWithQty) return firstWithQty.id

      // Rule 2: first available color
      return this.availableColors[0].id
    },

    // Active color = explicit user selection OR default
    activeColorId() {
      return this.expandedColorId || this.defaultActiveColorId
    },

    isQtyOnlyProduct() {
      return this.availableSizes.length === 1 && this.availableSizes[0] === 'qty'
    },

    isDefaultQtyToSecondColumnEnabled() {
      return window?.PXUTheme?.theme_settings?.qty_default_second_column_enabled === true
    },

    secondColumnQty() {
      const breaks = this.imprintStore.currentMethodQuantityBreaks || []
      if (breaks.length > 1) return breaks[1]
      if (breaks.length === 1) return breaks[0]
      return this.minQuantity
    },

    colorRows() {
      const rows = []
      for (let i = 0; i < this.availableColors.length; i += this.columnsPerRow) {
        rows.push(this.availableColors.slice(i, i + this.columnsPerRow))
      }
      return rows
    },

    availableSizes() {
        if (useMockData()) {
            const codes = this.imprintStore.productData.fullJson.varies_by.map(v => v.CODE);

            return (codes.includes("COLOR") && codes.includes("SIZE"))
                ? ['S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL']
                : ['qty'];
        }
      
      // Get from store cache
      const cached = this.selectionStore.getCachedApiData()
      const sizeVariants = cached.sizeVariants || []
      
      // Extract unique sizes from sizeVariants
      const sizes = []
      sizeVariants.forEach(variant => {
        if (variant.title && !sizes.includes(variant.title)) {
          sizes.push(variant.title)
        }
      })
      
      // If no sizes (regular product without sizes) - return ['qty']
      if (sizes.length === 0) {
        return ['qty']
      }
      
      return sizes
    },

    selectedLines() {
      return this.selectionStore.getLinesByImprintKey(this.imprintKey)
    },

    totalQuantity() {
      return this.selectionStore.getTotalQtyByImprintKey(this.imprintKey)
    },

    remainingQuantity() {
      return this.minQuantity - this.totalQuantity
    },

    canProceed() {
      return this.totalQuantity >= this.minQuantity
    },

    showValidation() {
      // Show validation only when quantity is less than minimum
      return this.totalQuantity < this.minQuantity
    },

    validationClass() {
      return this.canProceed ? 'success' : 'warning'
    },

    validationMessage() {
      return `${this.remainingQuantity} more items until minimum quantity`
    }
  },

  watch: {
    imprintKey: {
      handler(newKey, oldKey) {
        console.log('[ColorSizeSection] imprintKey changed:', oldKey, '->', newKey)
        if (newKey && newKey !== 'default-imprint') {
          this.selectionStore.initImprintKey(newKey)
        }
      },
      immediate: true
    },

    selectedLines: {
      handler(lines) {
        this.updateColorQuantities()
      },
      deep: true,
      immediate: true
    },
    
    activeColorId() {
      this.$nextTick(() => {
        this.updateArrowPosition()
      })
    },

    resetTrigger() {
      // Called when navigating back to Step 1 — reset to defaultActiveColorId
      this.expandedColorId = null
      setTimeout(() => {
        this.updateArrowPosition()
      }, 350)
    },

    expandedColorId() {
      // expandedColorId changed — activeColorId updates automatically via computed
      // updateArrowPosition is triggered via the activeColorId watcher
    },

    // Reset expandedColorId when section state changes
    // so activeColorId falls back to defaultActiveColorId (client requirements)
    'imprintStore.sections': {
      handler(sections) {
        const colourSection = sections?.find(s => s.id === 'colourQuantity')
        const isOpen = colourSection?.isOpen ?? true
        if (!isOpen) {
          // Section closed — clear explicit user selection
          this.expandedColorId = null
        } else {
          setTimeout(() => {
            this.updateArrowPosition()
          }, 350)
        }
      },
      deep: true
    }
  },

  async mounted() {
    console.log('[ColorSizeSection] Component mounted');
    
    // ✅ CRITICAL: Restore selection data after redirect/reload
    const restored = this.selectionStore.restoreFromSessionStorage();
    if (restored) {
      console.log('[ColorSizeSection] ✅ Restored selection data from sessionStorage');
    }
    
    this.selectionStore.initImprintKey(this.imprintKey)
    
    // Load data
    await this.loadData()
    this.isDataLoaded = true
    
    this.selectionStore.setAvailableColorsList(this.availableColors)
    this.updateColorQuantities()
    this.calculateColumnsPerRow()
    
    // Automatically select first color for isQtyOnly products
    if (this.isQtyOnlyProduct && this.availableColors.length > 0) {
      this.$nextTick(() => {
        this.initializeFirstColorForQtyOnly()
      })
    }
    
    window.addEventListener('resize', this.handleResize)
    // Add click outside listener
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    // Remove click outside listener
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    handleClickOutside(event) {
      // Selector is always visible via activeColorId — nothing to close
    },

    async loadData() {
      if (useMockData()) {
        console.log('[ColorSizeSection] Using mock data')
        return
      }

      try {
        // Отримуємо associated IDs
        await this.getAssociatedIds()
        
        // Завантажуємо кольори якщо є id_color
        if (this.associatedIds && this.associatedIds.simples && this.associatedIds.simples.id_color) {
          await this.getProductById(this.associatedIds.simples.id_color, 'color')
        }

        // Завантажуємо розміри якщо є id_size
        if (this.associatedIds && this.associatedIds.simples && this.associatedIds.simples.id_size) {
          await this.getProductById(this.associatedIds.simples.id_size, 'size')
        }
      } catch (error) {
        console.error('[ColorSizeSection] Error loading data:', error)
      }
    },

    getAssociatedIds() {
      try {
        const element = document.getElementById("associated_id")
        if (element && element.innerHTML) {
          this.associatedIds = JSON.parse(element.innerHTML)
          console.log('[ColorSizeSection] Associated IDs:', this.associatedIds)
          
          // Якщо id_color це масив, беремо останній елемент
          if (Array.isArray(this.associatedIds.simples.id_color)) {
            this.associatedIds.simples.id_color = this.associatedIds.simples.id_color[this.associatedIds.simples.id_color.length - 1]
          }
          
          // FIXED: Save to appStore for use in Add to Cart
          this.appStore.setAssociatedIds(this.associatedIds)
        }
      } catch (error) {
        console.error('[ColorSizeSection] Error parsing associated IDs:', error)
      }
    },

    async getProductById(id, type) {
      try {
        if (!id) {
          console.warn(`[ColorSizeSection] No ID provided for type: ${type}`)
          return
        }

        // --- color ---
        if (type === 'color' && !this.colorHandle) {
          this.colorHandle = await this.resolveProductHandle(id, 'color')
          if (!this.colorHandle) return
        }

        // --- size ---
        if (type === 'size' && !this.sizeHandle) {
          this.sizeHandle = await this.resolveProductHandle(id, 'size')
          if (!this.sizeHandle) return
        }

        // Get variants by handle
        if (type === 'color' && this.colorHandle) {
          if (!this.colorVariants) this.colorVariants = []
          if (this.colorVariants.length === 0) {
            await this.getColorVariants(this.colorHandle)
          }
        }

        if (type === 'size' && this.sizeHandle) {
          if (!this.sizeVariants) this.sizeVariants = []
          if (this.sizeVariants.length === 0) {
            await this.getSizeVariants(this.sizeHandle)
          }
        }
      } catch (error) {
        console.error(`[ColorSizeSection] Error getting product by ID (${type}):`, error)
      }
    },

    /**
     * Resolve a simple product's handle, in priority order:
     *   1) full_product_info.associated_ids.simples.handle_color / handle_size
     *      — no network call, set by integrator after UNLISTED migration.
     *   2) associated_id DOM element simples.handle_color / handle_size
     *      — same data, read directly from DOM (fallback if fullJson not loaded yet).
     *   3) /search/suggest.json — works only for active/listed products.
     *   4) backend endpoint for UNLISTED products without a stored handle.
     */
    async resolveProductHandle(id, type) {
      const handleKey = type === 'color' ? 'handle_color' : 'handle_size'

      // 1. from fullJson (loaded into imprintStore)
      const fullJson = this.imprintStore?.fullJson
      const handleFromFullJson = fullJson?.associated_ids?.simples?.[handleKey]
      if (handleFromFullJson) {
        console.log(`[ColorSizeSection] resolveProductHandle: from fullJson (${type}):`, handleFromFullJson)
        return handleFromFullJson
      }

      // 2. from associated_id DOM element (already parsed in getAssociatedIds)
      const handleFromDom = this.associatedIds?.simples?.[handleKey]
      if (handleFromDom) {
        console.log(`[ColorSizeSection] resolveProductHandle: from associated_id DOM (${type}):`, handleFromDom)
        return handleFromDom
      }

      // 3. suggest.json fallback (active/listed products only)
      try {
        const response = await axios.get(`/search/suggest.json?q=id:${id}&resources[type]=product&limit=1`)
        const products = response?.data?.resources?.results?.products
        if (products && products.length > 0) {
          console.log(`[ColorSizeSection] resolveProductHandle: from suggest.json (${type}):`, products[0].handle)
          return products[0].handle
        }
      } catch (e) {
        console.warn(`[ColorSizeSection] resolveProductHandle: suggest.json failed (${type})`, e)
      }

      // 4. backend endpoint for UNLISTED products without stored handle
      const handleFromBackend = await this.resolveFromBackend(id, type)
      if (handleFromBackend) {
        console.log(`[ColorSizeSection] resolveProductHandle: from backend (${type}):`, handleFromBackend)
        return handleFromBackend
      }

      console.warn(`[ColorSizeSection] resolveProductHandle: no handle found for id=${id} type=${type}`)
      return null
    },

    /**
     * Backend fallback via imprint-app ProductInfoController (GET /api/product/variants).
     * Called when the stored handle, fullJson, associated_id DOM data, and
     * suggest.json all come up empty (UNLISTED products without a stored handle yet).
     *
     * @param {string|number} id   - Shopify numeric product ID
     * @param {string}        type - 'color' or 'size' (used for logging only)
     * @returns {Promise<string|null>}
     */
    async resolveFromBackend(id, type) {
      try {
        const settings = window.PXUTheme?.theme_settings || {}
        const appUrl = settings.api_beckend_url || ''
        const shopDomain = settings.api_shop_domain || ''
        const shopName = shopDomain.replace('.myshopify.com', '')

        if (!appUrl || !shopDomain) {
          console.warn('[ColorSizeSection] resolveFromBackend: missing api_beckend_url or api_shop_domain theme setting')
          return null
        }

        const base = appUrl.replace(/\/+$/, '')
        const response = await axios.get(`${base}/api/product/variants`, {
          params: {
            product_id: id,
            shop_domain: shopDomain,
            shop: shopName
          }
        })

        return response?.data?.handle || null
      } catch (error) {
        console.warn(`[ColorSizeSection] resolveFromBackend error (${type}):`, error)
        return null
      }
    },

    async getColorVariants(handle) {
      try {
        if (!handle) {
          console.warn('[ColorSizeSection] No handle provided for color variants')
          return
        }

        // Завантажуємо ВСІ варіанти одразу (без пагінації на рівні API)
        const response = await axios.get(window.Shopify.routes.root + 'products/' + handle + '.js')
        
        // Check response structure with null safety
        if (response?.data?.variants && Array.isArray(response.data.variants)) {
          // Filter temporary variants
          this.colorVariants = response.data.variants.filter(item => 
            item && item.title && !item.title.includes("_temp_")
          )
          console.log('[ColorSizeSection] Total color variants loaded:', this.colorVariants.length)
          
          // Cache in store - зберігаємо ВСІ варіанти
          this.selectionStore.setCachedApiData({ colorVariants: this.colorVariants })
        } else {
          console.warn('[ColorSizeSection] Invalid response structure for color variants')
          this.colorVariants = []
        }
      } catch (error) {
        console.error('[ColorSizeSection] Error loading color variants:', error)
        this.colorVariants = []
      }
    },

    loadMoreColors() {
      if (this.isLoadingMore || !this.hasMoreColors) {
        return
      }

      this.isLoadingMore = true
      
      // Simulate loading delay for better UX
      setTimeout(() => {
        this.currentPage++
        this.isLoadingMore = false
        
        console.log('[ColorSizeSection] Loaded more colors. Current page:', this.currentPage, 
                    'Showing:', this.availableColors.length, 'of', this.totalAvailableColors)
      }, 300)
    },

    async getSizeVariants(handle) {
      try {
        if (!handle) {
          console.warn('[ColorSizeSection] No handle provided for size variants')
          return
        }

        const response = await axios.get(window.Shopify.routes.root + 'products/' + handle + '.js')
        
        // Check response structure with null safety
        if (response?.data?.variants && Array.isArray(response.data.variants)) {
          this.sizeVariants = response.data.variants
          console.log('[ColorSizeSection] Size variants loaded:', this.sizeVariants.length)
          
          // Cache in store
          this.selectionStore.setCachedApiData({ sizeVariants: this.sizeVariants })
        } else {
          console.warn('[ColorSizeSection] Invalid response structure for size variants')
          this.sizeVariants = []
        }
      } catch (error) {
        console.error('[ColorSizeSection] Error loading size variants:', error)
        this.sizeVariants = []
      }
    },

    setGridRef(el, rowIndex) {
      if (el) {
        this.gridRefs[rowIndex] = el
      }
    },

    setColorCardRef(el, colorId) {
      if (el) {
        this.colorCardRefs[colorId] = el
      }
    },

    setLineWrapperRef(el, rowIndex) {
      if (el) {
        this.lineWrapperRefs[rowIndex] = el
      }
    },

    updateArrowPosition() {
      const activeId = this.activeColorId
      if (!activeId) {
        this.arrowPosition = '0px'
        return
      }

      const cardEl = this.colorCardRefs[activeId]
      if (!cardEl || !cardEl.$el) {
        return
      }

      const cardDom = cardEl.$el
      const gridContainer = cardDom.closest('.colors-grid')
      if (!gridContainer) {
        return
      }

      const cardRect = cardDom.getBoundingClientRect()
      const gridRect = gridContainer.getBoundingClientRect()
      
      const relativeLeft = cardRect.left - gridRect.left
      const cardCenter = relativeLeft + (cardRect.width / 2)
      
      if (!this.isQtyOnlyProduct) {
        this.arrowPosition = `${cardCenter}px`
      }

      if (this.isQtyOnlyProduct) {
        let rowIndex = -1
        for (let i = 0; i < this.colorRows.length; i++) {
          const idx = this.colorRows[i].findIndex(c => c.id === activeId)
          if (idx !== -1) {
            rowIndex = i
            break
          }
        }

        if (rowIndex !== -1) {
          const lineWrapper = this.lineWrapperRefs[rowIndex]
          if (lineWrapper) {
            const offsetLeft = cardRect.left - gridRect.left
            lineWrapper.style.paddingLeft = `${offsetLeft}px`
          }
        }
      }
    },

    handleResize() {
      this.calculateColumnsPerRow()
      this.$nextTick(() => {
        this.updateArrowPosition()
      })
    },

    getExpandedColorInRow(row) {
      const activeId = this.activeColorId
      if (!activeId) return null

      const expandedColor = row.find(color => color.id === activeId)
      if (!expandedColor) return null

      // Ensure tempLineData exists for activeColorId (create if missing)
      if (!this.tempLineData[activeId]) {
        this.ensureTempLineData(expandedColor)
      }

      const lineFromStore = this.selectedLines.find(line => {
        return line && line.slectedColor && line.slectedColor.id === activeId
      })

      return lineFromStore || (this.tempLineData[activeId] ? true : null)
    },

    getExpandedLineData(row) {
      const activeId = this.activeColorId
      if (!activeId) return null

      const expandedColor = row.find(color => color.id === activeId)
      if (!expandedColor) return null

      const lineFromStore = this.selectedLines.find(line => {
        return line && line.slectedColor && line.slectedColor.id === activeId
      })

      return lineFromStore || this.tempLineData[activeId] || null
    },

    calculateColumnsPerRow() {
      const container = this.$el?.querySelector('.colors-grid');
       const width = window.innerWidth
      if (!container || width <=460) {
        const width = window.innerWidth
        if (width >= 1400) {
          this.columnsPerRow = 6
        } else if (width >= 1200) {
          this.columnsPerRow = 5
        } else if (width >= 992) {
          this.columnsPerRow = 4
        } else if (width >= 460) {
          this.columnsPerRow = 3
        } else {
          this.columnsPerRow = 2
        }
        return
      }

      const containerWidth = container.offsetWidth
      const gap = 16
      const minCardWidth = 112
      
      const cols = Math.floor((containerWidth + gap) / (minCardWidth + gap))
      this.columnsPerRow = Math.max(1, Math.min(cols, 6))
    },

    updateColorQuantities() {
      const newQuantities = {}
      this.selectedLines.forEach(line => {
        // Add null safety check
        if (line && line.slectedColor && line.slectedColor.id) {
          newQuantities[line.slectedColor.id] = line.totalQty || 0
        }
      })
      this.colorQuantities = { ...newQuantities }
    },

    isColorSelected(colorId) {
      return this.selectionStore.hasColorSelected(this.imprintKey, colorId)
    },

    isColorExpanded(colorId) {
      return this.activeColorId === colorId
    },

    handleColorSelect(color) {
      const isExpanded = this.expandedColorId === color.id
      const lineInStore = this.selectedLines.find(line => {
        return line && line.slectedColor && line.slectedColor.id === color.id
      })

      console.log('[ColorSizeSection] handleColorSelect:', {
        colorId: color.id,
        isExpanded,
        expandedColorId: this.expandedColorId,
        lastActiveColorId: this.lastActiveColorId,
        userModifiedColors: Array.from(this.userModifiedColors),
        autoInitializedColors: Array.from(this.autoInitializedColors)
      })

      if (isExpanded) {
        // User clicked the active card — do nothing, keep selector visible
        return
      } else {
        // Expanding a color (could be same as lastActive or different)
        
        // For isQtyOnly: remove previous color if it wasn't modified by user
        if (this.isQtyOnlyProduct && this.lastActiveColorId && this.lastActiveColorId !== color.id) {
          const previousLine = this.selectedLines.find(line => {
            return line && line.slectedColor && line.slectedColor.id === this.lastActiveColorId
          })
          
          const isModified = this.userModifiedColors.has(this.lastActiveColorId)
          console.log('[ColorSizeSection] Previous color check:', {
            previousColorId: this.lastActiveColorId,
            previousColorIdType: typeof this.lastActiveColorId,
            isModified,
            userModifiedColors: Array.from(this.userModifiedColors),
            userModifiedColorsTypes: Array.from(this.userModifiedColors).map(id => typeof id),
            willRemove: previousLine && !isModified
          })
          
          // Remove previous line only if user didn't modify it
          if (previousLine && !isModified) {
            this.selectionStore.removeColorSizeLine(this.imprintKey, previousLine.id)
            // Also remove from auto-initialized set
            this.autoInitializedColors.delete(this.lastActiveColorId)
            console.log('[ColorSizeSection] Removed previous color (not modified by user):', this.lastActiveColorId)
          }
        }

        if (!lineInStore) {
          const defaultSizes = {}
          this.availableSizes.forEach(size => {
            // Commented out: For isQtyOnly products, set minQuantity as default
            // defaultSizes[size] = this.isQtyOnlyProduct ? this.minQuantity : 0
            defaultSizes[size] = 0
          })

          // Create proper slectedColor structure matching old store
          this.tempLineData[color.id] = {
            id: `temp-${color.id}`,
            slectedColor: {
              id: color.id,
              title: color.title || color.option1 || '',
              img_src: color.featured_image?.src || color.image || '',
              img_id: color.featured_image?.id || color.image_id || null,
              img_sku: color.sku || '',
              img_gid: color.featured_media?.id || null,
              alt: color.featured_image?.alt || color.title || '',
              hex: color.hex || color.color_hex || null
            },
            selectedSizes: defaultSizes,
            // Commented out: Set totalQty to minQuantity for isQtyOnly
            // totalQty: this.isQtyOnlyProduct ? this.minQuantity : 0
            totalQty: 0
          }
          
          // Commented out: For isQtyOnly, immediately add to store (but don't mark as modified)
          // if (this.isQtyOnlyProduct) {
          //   this.selectionStore.addColorSizeLine(this.imprintKey, {
          //     slectedColor: this.tempLineData[color.id].slectedColor,
          //     selectedSizes: this.tempLineData[color.id].selectedSizes,
          //     totalQty: this.tempLineData[color.id].totalQty
          //   })
          //   delete this.tempLineData[color.id]
          //   // Mark as auto-initialized (not user-modified)
          //   this.autoInitializedColors.add(color.id)
          //   console.log('[ColorSizeSection] Added color to store (marked as auto-initialized):', color.id)
          // }
        }
        
        // Update both expanded and lastActive
        this.expandedColorId = color.id
        this.lastActiveColorId = color.id
      }
    },

    handleUpdateQuantity({ lineId, size, quantity }) {
      const isTemp = lineId.toString().startsWith('temp-')
      
      if (isTemp) {
        const colorId = Number(lineId.toString().replace('temp-', ''))
        const tempLine = this.tempLineData[colorId]
        
        if (tempLine) {
          tempLine.selectedSizes[size] = quantity
          tempLine.totalQty = Object.values(tempLine.selectedSizes).reduce((sum, q) => sum + (Number(q) || 0), 0)
          
          // Always add to store when quantity changes (even if goes to 0)
          // This ensures the line is saved before switching colors
            if (tempLine.totalQty > 0) {
                this.selectionStore.addColorSizeLine(this.imprintKey, {
                    slectedColor: tempLine.slectedColor,
                    selectedSizes: tempLine.selectedSizes,
                    totalQty: tempLine.totalQty
                })

                // Mark as user-modified since they changed the quantity
                // Use Number() to ensure consistent type with color.id
                this.userModifiedColors.add(colorId)
                console.log('[ColorSizeSection] Color marked as user-modified (from temp):', colorId, 'type:', typeof colorId)

                delete this.tempLineData[colorId]
            }
        }
      } else {
        // Find the line and check if it was auto-initialized
        const line = this.selectedLines.find(l => l.id === lineId)
        if (line && line.slectedColor) {
          const colorId = line.slectedColor.id
          
          // Only mark as modified if this is actual user interaction (not auto-initialization)
          if (!this.autoInitializedColors.has(colorId)) {
            // This is a user modification
            this.userModifiedColors.add(colorId)
            console.log('[ColorSizeSection] Color marked as user-modified:', colorId)
          } else {
            // First modification after auto-initialization
            this.autoInitializedColors.delete(colorId)
            this.userModifiedColors.add(colorId)
            console.log('[ColorSizeSection] Color changed from auto-initialized to user-modified:', colorId)
          }
        }
        
        this.selectionStore.updateLineSizeQty(this.imprintKey, lineId, size, quantity)
        
        const updatedLine = this.selectedLines.find(l => l.id === lineId)
        if (updatedLine && updatedLine.totalQty === 0) {
          this.selectionStore.removeColorSizeLine(this.imprintKey, lineId)
          
          // Remove from both sets if deleted
          if (updatedLine.slectedColor) {
            this.userModifiedColors.delete(updatedLine.slectedColor.id)
            this.autoInitializedColors.delete(updatedLine.slectedColor.id)
          }
          
          // Add null safety check
          if (updatedLine.slectedColor && updatedLine.slectedColor.id && this.expandedColorId === updatedLine.slectedColor.id) {
            this.tempLineData[updatedLine.slectedColor.id] = {
              id: `temp-${updatedLine.slectedColor.id}`,
              slectedColor: updatedLine.slectedColor,
              selectedSizes: updatedLine.selectedSizes,
              totalQty: 0
            }
          }
        }
      }
    },

    handleRemoveLine(lineId) {
      const isTemp = lineId.toString().startsWith('temp-')
      
      if (isTemp) {
        const colorId = lineId.replace('temp-', '')
        delete this.tempLineData[colorId]
        
        // if (this.expandedColorId === colorId) {
          this.expandedColorId = null
        // }
      } else {
        const line = this.selectedLines.find(l => l.id === lineId)
        
        this.selectionStore.removeColorSizeLine(this.imprintKey, lineId)
        
        // Remove from both sets when line is removed
        if (line && line.slectedColor) {
          this.userModifiedColors.delete(line.slectedColor.id)
          this.autoInitializedColors.delete(line.slectedColor.id)
        }
        
        if (line && line.slectedColor && this.expandedColorId === line.slectedColor.id) {
          this.expandedColorId = null
        }
      }
    },

    handleNextStep() {
      console.log('[ColorSizeSection] Next Step button clicked');
      // Emit event (button is disabled when !canProceed, so this only fires when valid)
      this.$emit('next-step');
    },

    // Reset expanded color selection to defaultActiveColorId (called externally via ref)
    resetColorSelection() {
      this.expandedColorId = null
    },

    // Ensure tempLineData exists for a color (called from getExpandedColorInRow)
    ensureTempLineData(color) {
      if (this.tempLineData[color.id]) return

      const existingLine = this.selectedLines.find(line => {
        return line && line.slectedColor && line.slectedColor.id === color.id
      })
      if (existingLine) return

      const defaultSizes = {}
      this.availableSizes.forEach(size => { defaultSizes[size] = 0 })

      this.tempLineData[color.id] = {
        id: `temp-${color.id}`,
        slectedColor: {
          id: color.id,
          title: color.title || color.option1 || '',
          img_src: color.featured_image?.src || color.image || '',
          img_id: color.featured_image?.id || color.image_id || null,
          img_sku: color.sku || '',
          img_gid: color.featured_media?.id || null,
          alt: color.featured_image?.alt || color.title || '',
          hex: color.hex || color.color_hex || null
        },
        selectedSizes: defaultSizes,
        totalQty: 0
      }
    },

    initializeFirstColorForQtyOnly() {
      if (!this.isQtyOnlyProduct || this.availableColors.length === 0) {
        return
      }

      const firstColor = this.availableColors[0]
      
      // Check if line already exists for this color
      const existingLine = this.selectedLines.find(line => {
        return line && line.slectedColor && line.slectedColor.id === firstColor.id
      })

      if (existingLine) {
        // If line already exists, just expand it
        this.expandedColorId = firstColor.id
        this.lastActiveColorId = firstColor.id
      } else {
        const defaultQtyValue = this.isDefaultQtyToSecondColumnEnabled ? this.secondColumnQty : 0

        const defaultSizes = {}
        this.availableSizes.forEach(size => {
          defaultSizes[size] = defaultQtyValue
        })

        // Create line structure
        const newLine = {
          slectedColor: {
            id: firstColor.id,
            title: firstColor.title || firstColor.option1 || '',
            img_src: firstColor.featured_image?.src || firstColor.image || '',
            img_id: firstColor.featured_image?.id || firstColor.image_id || null,
            img_sku: firstColor.sku || '',
            img_gid: firstColor.featured_media?.id || null,
            alt: firstColor.featured_image?.alt || firstColor.title || '',
            hex: firstColor.hex || firstColor.color_hex || null
          },
          selectedSizes: defaultSizes,
          totalQty: defaultQtyValue
        }

        if (defaultQtyValue > 0) {
          this.selectionStore.addColorSizeLine(this.imprintKey, {
            slectedColor: newLine.slectedColor,
            selectedSizes: newLine.selectedSizes,
            totalQty: newLine.totalQty
          })

          this.userModifiedColors.add(firstColor.id)
          console.log('[ColorSizeSection] First color default qty committed as user-modified:', firstColor.id)
        } else {
          this.tempLineData[firstColor.id] = {
            id: `temp-${firstColor.id}`,
            slectedColor: newLine.slectedColor,
            selectedSizes: newLine.selectedSizes,
            totalQty: newLine.totalQty
          }

          // Mark as auto-initialized (not user-modified) since nothing was committed
          this.autoInitializedColors.add(firstColor.id)
          console.log('[ColorSizeSection] First color auto-initialized (no default qty):', firstColor.id)
        }
        
        // Expand this color and set as last active
        this.expandedColorId = firstColor.id
        this.lastActiveColorId = firstColor.id
      }

      // Update arrow position
      this.$nextTick(() => {
        this.updateArrowPosition()
      })
    }
  }
}
</script>

<style scoped lang="scss">
@use '@/styles/components/color-size-section';
</style>

